import { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import Row from '@app/components/layout/Row'
import Column from '@app/components/layout/Column'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Tag, TagProps } from '@app/components/core/tag/Tag'
import { Button, ButtonProps } from '@app/components/core/button/Button'
import { Style } from 'inlines'
import { TextLink } from '@app/components/core/link/TextLink'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { pages } from '@app/util/links'
import { Badge } from '@app/components/core/badge/Badge'
import { useLocation } from 'wouter'
import useMediaQuery from '@app/util/useMediaQuery'

export type PageHeaderProps = {
  title: string
  subtitle?: string
  buttons?: ButtonProps[]
  tags?: TagProps[]
  hasBadge?: boolean
  style?: Style
  onClick?: () => void
  profile?: ProfileProps
}

export type HeaderIconProps = {
  icon: IconDefinition
  size: 'small' | 'medium' | 'large' | 'xlarge'
  position: 'left' | 'right'
  color?: string
}

export type ProfileProps = {
  avatar?: { src: string }
  title: string
  slug: string
}

export const PageHeader = ({
  title,
  subtitle,
  buttons,
  tags,
  hasBadge,
  style,
  profile,
}: PageHeaderProps) => {
  const [, navigate] = useLocation()
  const isMobile = useMediaQuery()

  return (
    <Column
      gap="1rem"
      style={{
        flex: '1 0 0',
        width: '100%',
        ...style,
      }}
    >
      <Column
        gap="0.5rem"
        style={{
          width: '100%',
        }}
      >
        <Row style={{ width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <Row gap="1rem" style={{ alignItems: 'center' }}>
            <Title.Page styledAsLevel={2} style={{ marginBottom: 0 }}>
              {title}
            </Title.Page>
            {hasBadge && <Badge style={{ height: '2rem', width: '2rem' }} />}
          </Row>

          {buttons && (
            <Row style={{ marginLeft: isMobile ? '0' : 'auto' }}>
              {[...buttons]
                .sort(
                  (a, b) =>
                    ((a as ButtonProps & { order?: number }).order ?? 0) -
                    ((b as ButtonProps & { order?: number }).order ?? 0),
                )
                .map((button, index) => (
                  <Button key={`${button.label}-${index}`} {...button} />
                ))}
            </Row>
          )}
        </Row>

        {profile && (
          <Row
            gap={'0.75rem'}
            style={{
              marginBottom: '0.5rem',
              alignItems: 'center',
            }}
          >
            <Text.Large style={{ marginBottom: 0 }}>{subtitle}</Text.Large>
            <Row
              gap={'0.35rem'}
              style={{ cursor: 'pointer', alignItems: 'center' }}
            >
              <Avatar
                image={profile.avatar?.src}
                size="extra-extra-small"
                shape="square"
                onClick={() =>
                  navigate(pages.regionalBusinessProfile(profile.slug))
                }
                style={{
                  border: 'var(--border-subtle)',
                }}
              />
              <TextLink
                to={pages.regionalBusinessProfile(profile.slug)}
                textStyle={{
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'var(--brand-secondary)',
                  textDecoration: 'none',
                }}
              >
                {profile.title}
              </TextLink>
            </Row>
          </Row>
        )}

        {!profile && subtitle && (
          <Text.Large style={{ marginBottom: '0.5rem' }}>{subtitle}</Text.Large>
        )}

        <Row gap="0.5rem">
          {tags?.map((tag, index) => (
            <Tag
              key={`${tag.label}-${index}`}
              {...tag}
              style={{ alignSelf: 'start' }}
            />
          ))}
        </Row>
      </Column>
    </Column>
  )
}
